import { useRef } from "react";
import PropTypes from "prop-types";
import styles from "./SizeGuideButton.scss";
import classnamesBind from "classnames/bind";
import { fireSizeGuideButtonClickAnalytics } from "../../analytics/pageInteraction";
import { track } from "../../utils/features";
import getWindow from "../../utils/window";
import { useSizing } from "../../context/sizing/SizingContext";

const classnames = classnamesBind.bind(styles);

export const SIZE_GUIDE_BUTTON_SIZE = {
  SMALL: "small",
  LARGE: "large",
};

export default function SizeGuideButton({
  sizeGuideUrl,
  text,
  size: sizeProp,
  sizeGuideExperimentBucket = "v0",
  entryPoint,
  forceDefaultStyle = false,
}) {
  const { setSizeGuideOpened } = useSizing();
  const buttonRef = useRef(null);

  const isV2 = sizeGuideExperimentBucket === "v2" && !forceDefaultStyle;
  const isExperimentVariant =
    sizeGuideExperimentBucket === "v1" || sizeGuideExperimentBucket === "v2";
  const excludedFromExperiment = sizeGuideExperimentBucket === "v0";

  const size = (() => {
    if (excludedFromExperiment || forceDefaultStyle) {
      return sizeProp || SIZE_GUIDE_BUTTON_SIZE.SMALL;
    }
    return isV2 ? SIZE_GUIDE_BUTTON_SIZE.LARGE : SIZE_GUIDE_BUTTON_SIZE.SMALL;
  })();

  const handleClick = () => {
    setSizeGuideOpened(true);
    if (isExperimentVariant) {
      track(entryPoint);
    }
    fireSizeGuideButtonClickAnalytics(entryPoint);
    getWindow().asos.eventBus.emit("sizeGuideOpen", {
      url: sizeGuideUrl,
      focusOnClose: buttonRef.current,
    });
  };

  if (!sizeGuideUrl) return null;

  return (
    <button
      className={classnames(
        styles.sizeGuideButton,
        { [size]: size },
        isV2 && styles.sizeGuideExperimentV2
      )}
      data-testid="size-guide-button"
      ref={buttonRef}
      onClick={handleClick}
    >
      {isV2 && (
        <span
          className={classnames(styles.iconSizeGuide, "product-ruler")}
          aria-hidden="true"
        />
      )}
      {text}
    </button>
  );
}

SizeGuideButton.propTypes = {
  sizeGuideUrl: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  size: PropTypes.oneOf([
    SIZE_GUIDE_BUTTON_SIZE.SMALL,
    SIZE_GUIDE_BUTTON_SIZE.LARGE,
  ]),
  sizeGuideExperimentBucket: PropTypes.oneOf(["v0", "v1", "v2"]),
  entryPoint: PropTypes.string.isRequired,
  forceDefaultStyle: PropTypes.bool,
};
